import request from '@/utils/request'
import { v2Host } from '@/config/host'
import { AxiosPromise, AxiosRequestConfig } from 'axios'
import { ResponseData } from 'request'
import {
  CheckAssetsData,
  searchConfig,
  ListAssets,
  AssetsUsage,
  AssetsTransProgress,
  AssetDetail,
  CheckAssetsResponse,
} from '@pt/api/assets/index.type'
import { cookRequestParams } from '@/common/request'

/**
 * 资源列表
 */
export function assetsList(
  data?: Partial<searchConfig>,
  config?: AxiosRequestConfig,
): AxiosPromise<ResponseData<ListAssets>> {
  return request({
    url: `${v2Host}/assets/list`,
    method: 'get',
    params: cookRequestParams(data),
    ...config,
  })
}

/**
 * 上传前校验
 * @param data.name 资源名称
 * @param data.format 文件类型，如：png
 * @param data.size 文件大小
 */
export function checkAssets(
  data: CheckAssetsData,
): AxiosPromise<ResponseData<CheckAssetsResponse>> {
  return request({
    url: `${v2Host}/assets/check`,
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    data,
    withoutError: 1, // 检测接口不需要弹出全局的错误弹框
  })
}

/**
 * 创建资源
 * @param data.name 资源名称
 * @param data.file_url 传上传返回的私有地址
 * @param data.description 资源描述
 * @param data.auto_rename 是否自动重命名
 * @param data.creative_type 3d_model
 * @param data.skeleton_type 骨骼类型
 */
export function createAssets(data: {
  name: string
  file_url: string
  description?: number
  auto_rename?: number
  creative_type?: string
  skeleton_type?: number
}): AxiosPromise<ResponseData<string>> {
  return request({
    url: `${v2Host}/assets/create`,
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    data,
  })
}

/**
 * 删除资源
 * @param ids 需要删除的资源 数组格式
 */
export function deleteAssets(
  ids: string[],
): AxiosPromise<ResponseData<string>> {
  return request({
    url: `${v2Host}/assets/delete`,
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    data: {
      ids,
    },
  })
}

/**
 * 资源详情
 * @param id 需要获取资源详情的 id
 */
export function getAssetDetail(
  id: number,
): AxiosPromise<ResponseData<AssetDetail>> {
  return request({
    url: `${v2Host}/assets/detail`,
    method: 'get',
    params: {
      id,
    },
  })
}

/**
 * 批量下载资源
 * @param ids 需要批量下载的资源 数组格式
 * 因为批量下载需要打包，后端会把资源打包后之后，返回下载链接；前端通过打开 response 的下载链接进行下载；
 */
export function downloadAssets(
  ids: string[],
  batch?: number,
): AxiosPromise<ResponseData<string>> {
  return request({
    url: `${v2Host}/assets/download`,
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    data: {
      ids,
      batch,
    },
  })
}

/**
 * 更新资源
 * @param data.id 资源id
 * @param data.name 资源名称
 * @param data.description 资源描述
 * @param data.cover_url 封面地址
 */
export function updateAssets(data: {
  id: string
  name?: string
  description?: string
  cover_url?: string
}): AxiosPromise<ResponseData<string>> {
  return request({
    url: `${v2Host}/assets/update`,
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    data,
  })
}

/**
 * 获取空间使用情况
 */
export function getAssetsUsage(): AxiosPromise<ResponseData<AssetsUsage>> {
  return request({
    url: `${v2Host}/assets/storage`,
    method: 'get',
  })
}

/**
 * 上传资源转码重试
 * @param asset_id 资源id
 */
export function getAssetRetryTranscode(
  asset_id: string,
): AxiosPromise<ResponseData<string>> {
  return request({
    url: `${v2Host}/assets/retry-transcode`,
    method: 'get',
    params: {
      asset_id,
    },
  })
}

/**
 * 资源转码状态
 * @param asset_ids 资源 id，通过逗号分隔
 */
export function getTranscodeStatus(
  asset_ids: string,
): AxiosPromise<ResponseData<AssetsTransProgress[]>> {
  return request({
    url: `${v2Host}/assets/transcode-status`,
    method: 'get',
    params: {
      asset_ids,
    },
  })
}
